import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FlatList, StyleSheet } from 'react-native';
import { Button } from 'react-native-paper';

import Separator from '@src/components/Separator';
import { separatorWidth, windowWidth } from '@src/configuration/constants';
import useAnimatedHeader from '@src/hooks/useAnimatedHeader';
import useIsMounted from '@src/hooks/useIsMounted';
import { Label } from '@src/types';

type Props = {
  ListHeaderComponent?: React.ComponentType<any> | React.ReactElement | null;
  name: string;
  labels: Label[];
  multiple?: boolean;
  split?: boolean;
};

const keyExtractor = (item: Label) => item.id.toString();

const styles = StyleSheet.create({
  contentContainer: {
    paddingBottom: separatorWidth * 5,
    flexGrow: 1,
  },
  buttonMultiple: {
    marginHorizontal: separatorWidth,
    width: windowWidth / 2 - separatorWidth * 2,
  },
  button: {
    marginHorizontal: separatorWidth,
  },
});

type State = { [key: number]: boolean };

const Component: React.FC<Props> = ({
  name,
  labels,
  multiple,
  split,
  ListHeaderComponent,
}) => {
  const { register, unregister, setValue } = useFormContext();
  const [selected, setSelected] = React.useState<State>({});
  const handleScroll = useAnimatedHeader();
  const isMounted = useIsMounted();

  React.useEffect(() => {
    register(name);
    return () => unregister(name);
  }, [name, register, unregister]);

  React.useEffect(() => {
    if (isMounted) {
      if (multiple) {
        setValue(name, Object.keys(selected));
      } else {
        setValue(name, Object.keys(selected)?.[0]);
      }
    }
  }, [isMounted, multiple, name, selected, setValue]);
  return (
    <FlatList<Label>
      onScroll={handleScroll}
      ListHeaderComponent={ListHeaderComponent}
      extraData={selected}
      renderItem={({ item }) => {
        const mode = selected?.[item.id] ? 'contained' : 'outlined';
        return (
          <Button
            mode={mode}
            style={split ? styles.buttonMultiple : styles.button}
            onPress={() => {
              if (multiple) {
                setSelected({
                  ...selected,
                  [item.id]: !(selected?.[item.id] ?? false),
                });
              } else {
                setSelected({
                  [item.id]: true,
                });
              }
            }}
          >
            {item.name}
          </Button>
        );
      }}
      keyExtractor={keyExtractor}
      ItemSeparatorComponent={Separator}
      data={labels}
      numColumns={split ? 2 : undefined}
      contentContainerStyle={styles.contentContainer}
    />
  );
};

Component.displayName = 'LabelInput';

export default Component;
